// =============================================================================
// Origin UI — src/pages/assets/ITAssetManagementPage.tsx
// Company-wide IT Asset Management page for ITOps and HRAdmin.
// Filterable, searchable, paginated table with full CRUD.
// =============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { assetApi, AssetListItem, AssetPagedResult, AssetDetail, EmployeeSearchItem } from '@/services/assetApi';
import { api } from '@/services/apiService';
import { ConfirmDialog } from '@/components/common';
import styles from './assets.module.css';

const ASSET_TYPES = ['Laptop', 'Desktop', 'Monitor', 'Phone', 'Headset', 'Licence', 'AccessCard', 'Other'];
const ASSET_STATUSES = ['Assigned', 'Unassigned', 'PendingReturn', 'Returned', 'Lost', 'Disposed'];
const ASSET_CONDITIONS = ['New', 'Good', 'Fair', 'Poor', 'Damaged'];

const TYPE_ICONS: Record<string, string> = {
  Laptop: '💻', Desktop: '🖥️', Monitor: '🖥️', Phone: '📱',
  Headset: '🎧', Licence: '🔑', AccessCard: '🪪', Other: '📦',
};

interface OfficeLocation {
  officeLocationId: number;
  name: string;
}

export function ITAssetManagementPage() {
  const { roles } = useAuth();
  const navigate = useNavigate();
  const isHR = roles.isHRAdmin;

  // ─── Data state ───
  const [data, setData] = useState<AssetPagedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offices, setOffices] = useState<OfficeLocation[]>([]);

  // ─── Filters ───
  const [search, setSearch] = useState('');
  const [office, setOffice] = useState('');
  const [assetType, setAssetType] = useState('');
  const [status, setStatus] = useState('');
  const [condition, setCondition] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // ─── CRUD state ───
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AssetListItem | null>(null);
  const [writeOffTarget, setWriteOffTarget] = useState<{ assetId: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // ─── Asset detail (history + attachments) ───
  const [assetDetail, setAssetDetail] = useState<AssetDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ─── Form ref (auto-scroll on open) ───
  const formRef = useRef<HTMLDivElement>(null);

  // ─── Employee search for Add/Edit form ───
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState<EmployeeSearchItem[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchItem | null>(null);
  const [empSearching, setEmpSearching] = useState(false);

  const emptyForm = {
    assetType: 'Laptop', makeModel: '', serialNumber: '', assetTag: '',
    status: 'Assigned', condition: 'New', assignedDate: new Date().toISOString().split('T')[0],
    returnedDate: '', purchaseDate: '', warrantyExpiryDate: '', notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  // ─── Load data ───
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const statusParam = status === 'Active' ? 'Active' : status;
      const result = await assetApi.getAll({ search, office, assetType, status: statusParam, condition, page, pageSize: 50, sortBy: sortBy || undefined, sortDir: sortBy ? sortDir : undefined });
      setData(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load assets';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [search, office, assetType, status, condition, page, sortBy, sortDir]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load offices for filter
  useEffect(() => {
    api.get<OfficeLocation[]>('/api/reference/office-locations').then(setOffices).catch(() => {});
  }, []);

  // ─── Search debounce ───
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  function handleSearchChange(value: string) {
    setSearch(value);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(() => { setPage(1); }, 400));
  }

  // ─── Employee search for form ───
  useEffect(() => {
    if (empSearch.length < 2) { setEmpResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        setEmpSearching(true);
        const results = await api.get<EmployeeSearchItem[]>(`/api/employees?search=${encodeURIComponent(empSearch)}`);
        setEmpResults(results);
      } catch { setEmpResults([]); }
      finally { setEmpSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [empSearch]);

  // ─── Auto-clear employee when status changes to Unassigned ───
  useEffect(() => {
    if (form.status === 'Unassigned') {
      setSelectedEmployee(null);
    }
  }, [form.status]);

  // ─── Scroll to form when it opens ───
  useEffect(() => {
    if ((showAddForm || editingAsset) && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [showAddForm, editingAsset]);

  // ─── CRUD handlers ───
  function handleAdd() {
    setEditingAsset(null);
    setSelectedEmployee(null);
    setEmpSearch('');
    setEmpResults([]);
    setForm(emptyForm);
    setShowAddForm(true);
  }

  function handleEdit(asset: AssetListItem) {
    setShowAddForm(false);
    setEditingAsset(asset);
    setAssetDetail(null);
    setLoadingDetail(true);
    assetApi.getDetail(asset.assetId)
      .then(detail => setAssetDetail(detail))
      .catch(() => setAssetDetail(null))
      .finally(() => setLoadingDetail(false));
    if (asset.employeeId && asset.employeeName) {
      const nameParts = asset.employeeName.split(' ');
      setSelectedEmployee({
        employeeId: asset.employeeId,
        employeeNumber: asset.employeeNumber || '',
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' '),
        preferredName: null,
        departmentName: asset.departmentName || '',
        officeName: asset.officeName || '',
      });
    } else {
      setSelectedEmployee(null);
    }
    setEmpSearch('');
    setEmpResults([]);
    setForm({
      assetType: asset.assetType,
      makeModel: asset.makeModel || '',
      serialNumber: asset.serialNumber || '',
      assetTag: asset.assetTag || '',
      status: asset.status,
      condition: asset.condition || '',
      assignedDate: asset.assignedDate ? asset.assignedDate.split('T')[0] : '',
      returnedDate: asset.returnedDate ? asset.returnedDate.split('T')[0] : '',
      purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
      warrantyExpiryDate: asset.warrantyExpiryDate ? asset.warrantyExpiryDate.split('T')[0] : '',
      notes: asset.notes || '',
    });
  }

  function handleCancelForm() {
    setShowAddForm(false);
    setEditingAsset(null);
    setAssetDetail(null);
    setSelectedEmployee(null);
    setEmpSearch('');
    setEmpResults([]);
  }

  async function handleMarkReturned(asset: AssetListItem) {
    try {
      await assetApi.updateAsset(asset.assetId, {
        employeeId: asset.employeeId,
        assetType: asset.assetType,
        makeModel: asset.makeModel || undefined,
        serialNumber: asset.serialNumber || undefined,
        assetTag: asset.assetTag || undefined,
        status: 'Returned',
        condition: asset.condition || undefined,
        assignedDate: asset.assignedDate,
        returnedDate: new Date().toISOString().split('T')[0],
        purchaseDate: asset.purchaseDate,
        warrantyExpiryDate: asset.warrantyExpiryDate,
        notes: asset.notes || undefined,
      });
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to mark as returned';
      setError(message);
    }
  }

  async function handleSave() {
    if (form.status === 'Assigned' && !editingAsset?.employeeId && !selectedEmployee) {
      setError('Please select an employee when status is Assigned');
      return;
    }

    // If changing to Disposed, confirm first
    if (form.status === 'Disposed') {
      const assetName = editingAsset
        ? `${editingAsset.assetType}${editingAsset.makeModel ? ` (${editingAsset.makeModel})` : ''}`
        : `${form.assetType}${form.makeModel ? ` (${form.makeModel})` : ''}`;
      setWriteOffTarget({ assetId: editingAsset?.assetId || '', name: assetName });
      return;
    }

    await doSave();
  }

  async function doSave() {
    try {
      setSaving(true);
      setError('');

      const employeeId = form.status === 'Unassigned'
        ? null
        : (selectedEmployee?.employeeId || editingAsset?.employeeId || null);

      if (editingAsset) {
        await assetApi.updateAsset(editingAsset.assetId, {
          employeeId,
          assetType: form.assetType,
          makeModel: form.makeModel || undefined,
          serialNumber: form.serialNumber || undefined,
          assetTag: form.assetTag || undefined,
          status: form.status,
          condition: form.condition || undefined,
          assignedDate: form.assignedDate || null,
          returnedDate: form.returnedDate || null,
          purchaseDate: form.purchaseDate || null,
          warrantyExpiryDate: form.warrantyExpiryDate || null,
          notes: form.notes || undefined,
        });
      } else {
        await assetApi.createAsset({
          employeeId,
          assetType: form.assetType,
          makeModel: form.makeModel || undefined,
          serialNumber: form.serialNumber || undefined,
          assetTag: form.assetTag || undefined,
          status: form.status,
          condition: form.condition || undefined,
          assignedDate: form.assignedDate || null,
          purchaseDate: form.purchaseDate || null,
          warrantyExpiryDate: form.warrantyExpiryDate || null,
          notes: form.notes || undefined,
        });
      }
      handleCancelForm();
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save asset';
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleWriteOff() {
    if (!writeOffTarget) return;
    setWriteOffTarget(null);
    await doSave();
  }

  async function handleCsvExport() {
    try {
      await assetApi.exportCsv({ office, assetType, status, condition });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'CSV export failed';
      setError(message);
    }
  }

  // ─── Helpers ───
  function getStatusClass(s: string) {
    switch (s) {
      case 'Assigned': return styles.statusAssigned;
      case 'Unassigned': return styles.statusUnassigned || styles.statusDraft;
      case 'PendingReturn': return styles.statusPendingReturn;
      case 'Returned': return styles.statusReturned;
      case 'Lost': return styles.statusLost;
      case 'Disposed': return styles.statusDisposed;
      default: return '';
    }
  }

  function getConditionClass(c: string | null) {
    if (!c) return '';
    switch (c) {
      case 'New': return styles.condNew;
      case 'Good': return styles.condGood;
      case 'Fair': return styles.condFair;
      case 'Poor': return styles.condPoor;
      case 'Damaged': return styles.condDamaged;
      default: return '';
    }
  }

  function formatDate(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function isWarrantyExpiring(d: string | null): boolean {
    if (!d) return false;
    const diff = (new Date(d).getTime() - Date.now()) / 86400000;
    return diff >= 0 && diff <= 90;
  }

  function isWarrantyExpired(d: string | null): boolean {
    if (!d) return false;
    return new Date(d) < new Date();
  }

  function getStatusLabel(s: string): string {
    return s === 'PendingReturn' ? 'Pending Return' : s;
  }

  function getRowClass(asset: AssetListItem): string {
    if (asset.status === 'PendingReturn') return `${styles.tableRow} ${styles.rowPendingReturn}`;
    if (asset.status === 'Unassigned') return styles.tableRow;
    if (asset.status !== 'Assigned') return `${styles.tableRow} ${styles.rowInactive}`;
    return styles.tableRow;
  }

  const totalPages = data ? Math.ceil(data.totalCount / data.pageSize) : 0;
  const isEmployeeRequired = form.status === 'Assigned';

  // ─── Sorting ───
  function handleSort(column: string) {
    if (sortBy === column) {
      // Same column — toggle desc → asc → off
      if (sortDir === 'desc') {
        setSortDir('asc');
      } else {
        // Was asc — clear sort back to default
        setSortBy('');
        setSortDir('desc');
      }
    } else {
      // New column — start descending
      setSortBy(column);
      setSortDir('desc');
    }
    setPage(1);
  }

  function SortableHeader({ label, column, width }: { label: string; column: string; width?: string }) {
    const active = sortBy === column;
    const arrow = !active ? '↕' : sortDir === 'desc' ? '↓' : '↑';
    return (
      <th
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...(width ? { width } : {}) }}
        onClick={() => handleSort(column)}
      >
        {label}
        <span style={{
          marginLeft: '4px',
          fontSize: '11px',
          color: active ? '#00B0B9' : '#CDD4DB',
          fontWeight: active ? 700 : 400,
        }}>{arrow}</span>
      </th>
    );
  }

  // ─── Reusable form content ───
  function renderFormContent() {
    return (
      <>
        {/* Employee selector */}
        <div style={{ marginBottom: '14px' }}>
          <label className={styles.formLabel}>
            Employee {isEmployeeRequired && <span style={{ color: '#F04B41' }}>*</span>}
            {!isEmployeeRequired && <span style={{ fontSize: '11px', color: '#9BA5B0', marginLeft: '6px' }}>(optional for {form.status} assets)</span>}
          </label>
          {selectedEmployee ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
              <span className={styles.empName}>{selectedEmployee.firstName} {selectedEmployee.lastName}</span>
              <span className={styles.empNum}>{selectedEmployee.employeeNumber}</span>
              <span style={{ fontSize: '12px', color: '#6B7885' }}>{selectedEmployee.officeName}</span>
              <button className={styles.cancelBtn} onClick={() => { setSelectedEmployee(null); setEmpSearch(''); }} style={{ padding: '2px 8px', fontSize: '11px' }}>Change</button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input
                className={styles.formInput}
                type="text"
                placeholder={isEmployeeRequired ? 'Type employee name or number...' : 'Type employee name or number (or leave blank)...'}
                value={empSearch}
                onChange={e => setEmpSearch(e.target.value)}
                style={{ width: '100%', maxWidth: '400px' }}
              />
              {empResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, maxWidth: '400px',
                  background: '#fff', border: '1px solid #E2E7EC', borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(10,34,64,0.08)', zIndex: 10, maxHeight: '200px', overflowY: 'auto',
                }}>
                  {empResults.map(e => (
                    <div
                      key={e.employeeId}
                      onClick={() => { setSelectedEmployee(e); setEmpSearch(''); setEmpResults([]); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #F1F4F6' }}
                      onMouseEnter={ev => (ev.currentTarget.style.background = '#F8FAFB')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = '#fff')}
                    >
                      <strong>{e.preferredName || e.firstName} {e.lastName}</strong>
                      <span style={{ color: '#9BA5B0', marginLeft: '8px' }}>{e.employeeNumber} · {e.officeName}</span>
                    </div>
                  ))}
                </div>
              )}
              {empSearching && <div style={{ fontSize: '12px', color: '#9BA5B0', marginTop: '4px' }}>Searching...</div>}
            </div>
          )}
        </div>
        {editingAsset && !selectedEmployee && editingAsset.employeeName && (
          <div style={{ marginBottom: '14px', fontSize: '13px', color: '#4A5568' }}>
            Currently assigned to <strong>{editingAsset.employeeName}</strong> ({editingAsset.employeeNumber})
            — select a different employee above to reassign
          </div>
        )}

        <div className={styles.formGrid}>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Asset Type *</label>
            <select className={styles.formInput} value={form.assetType} onChange={e => setForm({ ...form, assetType: e.target.value })}>
              {ASSET_TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t] || ''} {t}</option>)}
            </select>
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Make / Model</label>
            <input className={styles.formInput} value={form.makeModel} onChange={e => setForm({ ...form, makeModel: e.target.value })} placeholder="e.g. Dell Latitude 5540" />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Serial Number</label>
            <input className={styles.formInput} value={form.serialNumber} onChange={e => setForm({ ...form, serialNumber: e.target.value })} />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Asset Tag</label>
            <input className={styles.formInput} value={form.assetTag} onChange={e => setForm({ ...form, assetTag: e.target.value })} placeholder="e.g. OTI-LAP-025" />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Status *</label>
            <select className={styles.formInput} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {ASSET_STATUSES.map(s => <option key={s} value={s}>{getStatusLabel(s)}</option>)}
            </select>
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Condition</label>
            <select className={styles.formInput} value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}>
              <option value="">— Select —</option>
              {ASSET_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Assigned Date</label>
            <input className={styles.formInput} type="date" value={form.assignedDate} onChange={e => setForm({ ...form, assignedDate: e.target.value })} />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Returned Date</label>
            <input className={styles.formInput} type="date" value={form.returnedDate} onChange={e => setForm({ ...form, returnedDate: e.target.value })} />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Purchase Date</label>
            <input className={styles.formInput} type="date" value={form.purchaseDate} onChange={e => setForm({ ...form, purchaseDate: e.target.value })} />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Warranty Expiry</label>
            <input className={styles.formInput} type="date" value={form.warrantyExpiryDate} onChange={e => setForm({ ...form, warrantyExpiryDate: e.target.value })} />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Notes</label>
            <input className={styles.formInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional details..." />
          </div>
        </div>
        <div className={styles.formActions}>
          <button className={styles.cancelBtn} onClick={handleCancelForm}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingAsset ? 'Update Asset' : 'Add Asset'}
          </button>
        </div>
      </>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>IT Asset Management</h1>
          <div className={styles.pageSubtitle}>
            {data ? `${data.totalCount} asset${data.totalCount !== 1 ? 's' : ''} total` : 'Loading...'}
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={handleCsvExport}>📥 Export CSV</button>
          <button className={styles.primaryBtn} onClick={handleAdd}>+ Add Asset</button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button className={styles.errorDismiss} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search employee, asset tag, serial, make/model..."
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
        />
        <select className={styles.filterSelect} value={office} onChange={e => { setOffice(e.target.value); setPage(1); }}>
          <option value="">All Offices</option>
          {offices.map(o => <option key={o.officeLocationId} value={o.name}>{o.name}</option>)}
        </select>
        <select className={styles.filterSelect} value={assetType} onChange={e => { setAssetType(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {ASSET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className={styles.filterSelect} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="Active">Active (Assigned + Pending)</option>
          {ASSET_STATUSES.map(s => <option key={s} value={s}>{getStatusLabel(s)}</option>)}
        </select>
        <select className={styles.filterSelect} value={condition} onChange={e => { setCondition(e.target.value); setPage(1); }}>
          <option value="">All Conditions</option>
          {ASSET_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Add Form (above table — only for new assets) */}
      {showAddForm && (
        <div ref={formRef} className={styles.inlineForm}>
          {renderFormContent()}
        </div>
      )}

      {/* Asset Table */}
      <div className={styles.card}>
        {loading && !data ? (
          <div className={styles.emptyText}>Loading assets...</div>
        ) : data && data.items.length === 0 ? (
          <div className={styles.emptyText}>No assets found matching your filters.</div>
        ) : data && (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <SortableHeader label="Asset" column="asset" />
                    <SortableHeader label="Office" column="office" />
                    <SortableHeader label="Employee" column="employee" />
                    <SortableHeader label="Tag / Serial" column="tag" />
                    <SortableHeader label="Status" column="status" />
                    <SortableHeader label="Condition" column="condition" />
                    <SortableHeader label="Assigned" column="assigned" />
                    <SortableHeader label="Warranty" column="warranty" />
                    <th style={{ width: '130px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(a => {
                    const isEditing = editingAsset?.assetId === a.assetId;
                    return (
                      <React.Fragment key={a.assetId}>
                        <tr className={getRowClass(a)}>
                          <td>
                            <div className={styles.assetTypeCell}>
                              <span className={styles.assetTypeIcon}>{TYPE_ICONS[a.assetType] || '📦'}</span>
                              <div>
                                <div className={styles.assetName}>{a.assetType}</div>
                                {a.makeModel && <div className={styles.assetMeta}>{a.makeModel}</div>}
                              </div>
                            </div>
                          </td>
                          <td style={{ fontSize: '12px', color: '#6B7885' }}>{a.officeName || '—'}</td>
                          <td>
                            {a.employeeId && a.employeeName ? (
                              <div className={styles.empCell}>
                                {isHR ? (
                                  <a className={styles.empNameLink} onClick={() => navigate(`/employees/${a.employeeId}`)}>{a.employeeName}</a>
                                ) : (
                                  <span className={styles.empName}>{a.employeeName}</span>
                                )}
                                <span className={styles.empNum}>{a.employeeNumber}</span>
                              </div>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#9BA5B0', fontStyle: 'italic' }}>Unassigned</span>
                            )}
                          </td>
                          <td>
                            {a.assetTag && <div className={styles.assetTagCode}>{a.assetTag}</div>}
                            {a.serialNumber && <div className={styles.assetMeta}>{a.serialNumber}</div>}
                            {!a.assetTag && !a.serialNumber && <span className={styles.assetMeta}>—</span>}
                          </td>
                          <td>
                            <span className={`${styles.statusBadge} ${getStatusClass(a.status)}`}>
                              {getStatusLabel(a.status)}
                            </span>
                            {a.status === 'PendingReturn' && a.returnDueDate && (
                              <div className={styles.returnDue}>Due {formatDate(a.returnDueDate)}</div>
                            )}
                          </td>
                          <td>
                            {a.condition ? (
                              <span className={`${styles.condBadge} ${getConditionClass(a.condition)}`}>{a.condition}</span>
                            ) : '—'}
                          </td>
                          <td className={styles.cellDate}>{formatDate(a.assignedDate)}</td>
                          <td>
                            {a.warrantyExpiryDate ? (
                              <span className={
                                isWarrantyExpired(a.warrantyExpiryDate) ? styles.warrantyExpired
                                  : isWarrantyExpiring(a.warrantyExpiryDate) ? styles.warrantyExpiring
                                    : styles.cellDate
                              }>
                                {formatDate(a.warrantyExpiryDate)}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {a.status === 'PendingReturn' && (
                                <button className={styles.returnSmallBtn} onClick={() => handleMarkReturned(a)}>Returned</button>
                              )}
                              <button className={styles.editSmallBtn} onClick={() => isEditing ? handleCancelForm() : handleEdit(a)}>
                                {isEditing ? 'Close' : 'Edit'}
                              </button>

                            </div>
                          </td>
                        </tr>
                        {/* Inline edit form row — appears right after the asset being edited */}
                        {isEditing && (
                          <tr>
                            <td colSpan={9} style={{ padding: 0, border: 'none' }}>
                              <div ref={formRef} className={styles.inlineForm} style={{ margin: '0', borderRadius: '0', borderTop: '2px solid #00B0B9', borderBottom: '2px solid #00B0B9' }}>
                                <div style={{ marginBottom: '10px', fontSize: '13px', fontWeight: 600, color: '#0A2240' }}>
                                  Editing: {a.assetType}{a.makeModel ? ` — ${a.makeModel}` : ''}{a.assetTag ? ` (${a.assetTag})` : ''}
                                </div>
                                {renderFormContent()}

                                {/* ─── Asset History ─── */}
                                {loadingDetail && (
                                  <div style={{ marginTop: '16px', fontSize: '13px', color: '#9BA5B0' }}>Loading history...</div>
                                )}
                                {assetDetail && assetDetail.history.length > 0 && (
                                  <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #E2E7EC' }}>
                                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0A2240', marginBottom: '10px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                      Reassignment History
                                    </div>
                                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ borderBottom: '1px solid #E2E7EC' }}>
                                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Date</th>
                                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>From</th>
                                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>To</th>
                                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Condition</th>
                                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Changed By</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {assetDetail.history.map(h => (
                                          <tr key={h.assetHistoryId} style={{ borderBottom: '1px solid #F1F4F6' }}>
                                            <td style={{ padding: '8px', color: '#4A5568' }}>{formatDate(h.changedDate)}</td>
                                            <td style={{ padding: '8px', color: '#4A5568' }}>
                                              {h.fromEmployeeName || <span style={{ fontStyle: 'italic', color: '#9BA5B0' }}>Unassigned</span>}
                                              {h.fromEmployeeNumber && <span style={{ color: '#9BA5B0', marginLeft: '4px' }}>({h.fromEmployeeNumber})</span>}
                                            </td>
                                            <td style={{ padding: '8px', color: '#4A5568' }}>
                                              {h.toEmployeeName || <span style={{ fontStyle: 'italic', color: '#9BA5B0' }}>Unassigned</span>}
                                              {h.toEmployeeNumber && <span style={{ color: '#9BA5B0', marginLeft: '4px' }}>({h.toEmployeeNumber})</span>}
                                            </td>
                                            <td style={{ padding: '8px', color: '#4A5568' }}>{h.conditionFromEmployee || '—'}</td>
                                            <td style={{ padding: '8px', color: '#6B7885' }}>{h.changedByName}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {assetDetail && assetDetail.history.length === 0 && (
                                  <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #E2E7EC', fontSize: '12px', color: '#9BA5B0', fontStyle: 'italic' }}>
                                    No reassignment history for this asset.
                                  </div>
                                )}

                                {/* ─── Attachments ─── */}
                                {assetDetail && (
                                  <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #E2E7EC' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#0A2240', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        Attachments
                                      </div>
                                      <label style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        padding: '6px 14px', fontSize: '12px', fontWeight: 600,
                                        color: '#00B0B9', background: '#E0F7F8', borderRadius: '8px',
                                        cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1,
                                      }}>
                                        {uploading ? 'Uploading...' : '📎 Upload File'}
                                        <input
                                          type="file"
                                          style={{ display: 'none' }}
                                          disabled={uploading}
                                          onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file || !editingAsset) return;
                                            try {
                                              setUploading(true);
                                              await assetApi.uploadAttachment(editingAsset.assetId, file);
                                              const updated = await assetApi.getDetail(editingAsset.assetId);
                                              setAssetDetail(updated);
                                            } catch (err: unknown) {
                                              const message = err instanceof Error ? err.message : 'Upload failed';
                                              setError(message);
                                            } finally {
                                              setUploading(false);
                                              e.target.value = '';
                                            }
                                          }}
                                        />
                                      </label>
                                    </div>
                                    {assetDetail.attachments.length > 0 ? (
                                      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ borderBottom: '1px solid #E2E7EC' }}>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>File</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Size</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Uploaded</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>By</th>
                                            <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: '#9BA5B0', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {assetDetail.attachments.map(att => (
                                            <tr key={att.assetAttachmentId} style={{ borderBottom: '1px solid #F1F4F6' }}>
                                              <td style={{ padding: '8px', color: '#0A2240', fontWeight: 500 }}>📄 {att.fileName}</td>
                                              <td style={{ padding: '8px', color: '#6B7885' }}>{(att.fileSize / 1024).toFixed(0)} KB</td>
                                              <td style={{ padding: '8px', color: '#4A5568' }}>{formatDate(att.uploadedDate)}</td>
                                              <td style={{ padding: '8px', color: '#6B7885' }}>{att.uploadedByName}</td>
                                              <td style={{ padding: '8px', textAlign: 'right' }}>
                                                <button
                                                  style={{ background: 'none', border: 'none', color: '#00B0B9', cursor: 'pointer', fontSize: '12px', fontWeight: 600, marginRight: '8px' }}
                                                  onClick={() => editingAsset && assetApi.downloadAttachment(editingAsset.assetId, att.assetAttachmentId, att.fileName)}
                                                >
                                                  Download
                                                </button>
                                                <button
                                                  style={{ background: 'none', border: 'none', color: '#F04B41', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                                                  onClick={async () => {
                                                    if (!editingAsset || !confirm(`Delete ${att.fileName}?`)) return;
                                                    try {
                                                      await assetApi.deleteAttachment(editingAsset.assetId, att.assetAttachmentId);
                                                      const updated = await assetApi.getDetail(editingAsset.assetId);
                                                      setAssetDetail(updated);
                                                    } catch (err: unknown) {
                                                      const message = err instanceof Error ? err.message : 'Delete failed';
                                                      setError(message);
                                                    }
                                                  }}
                                                >
                                                  Delete
                                                </button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <div style={{ fontSize: '12px', color: '#9BA5B0', fontStyle: 'italic' }}>
                                        No files attached. Use the upload button to add receipts, warranty documents, or photos.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <span className={styles.pageInfo}>
                  Showing {(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.totalCount)} of {data.totalCount}
                </span>
                <div className={styles.pageButtons}>
                  <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                  <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={writeOffTarget !== null}
        title="Write Off Asset"
        message={`Write off ${writeOffTarget?.name}? This will mark the asset as Disposed. The record will be kept for audit purposes.`}
        confirmLabel="Write Off"
        confirmVariant="danger"
        onConfirm={handleWriteOff}
        onCancel={() => setWriteOffTarget(null)}
      />
    </div>
  );
}
